import { useEffect, useState } from 'react'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { BulkSheetFilter, FilterFooter, FilterInput } from '../common'
import {
  styled,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { ICON_SIZE } from '../../../../BulkSheet/const'

export type SelectFilter = {
  values: string[]
  includeBlank: boolean
}

type Props = CustomFilterProps & {
  getValue: (option: any) => string
  getLabel: (option: any) => string
  valueGetter?: (params: any) => any
  valueFormatter: (params: any) => string
  sortValues: (uiMeta: any, options: any, context: any) => any
  /** context[optionKey] からオプションを取得する（フィルター状態に関係なく全候補を表示） */
  optionKey?: string
  /**
   * API経由でフィルターに表示するオプションのUUIDリストを取得する
   * 定義されている場合、context[optionKey]からこのリストに含まれるオプションのみ表示する
   */
  getValuesForOptionFilter?: (context: any) => Promise<string[]>
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

export const ClientSideSelectFilter = ({
  model,
  onModelChange,
  getValue,
  getLabel,
  valueGetter,
  valueFormatter,
  sortValues,
  colDef,
  context,
  api,
  optionKey,
  getValuesForOptionFilter,
}: Props) => {
  // UI-local state (not part of filter model)
  const [text, setText] = useState('')
  const [options, setOptions] = useState<any[]>([])

  // Derive state from model (single source of truth)
  const allOptionValues = options.map(getValue)
  const values = model?.values ?? allOptionValues
  const includeBlank = model?.includeBlank ?? true
  const isAllSelected =
    !model || (values.length === allOptionValues.length && includeBlank)
  const isAllDeselected = model !== null && values.length === 0 && !includeBlank

  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。model 変更のたびにオプション再取得は不要（afterGuiAttached で取得する）
  useEffect(() => {
    getOptions().then(setOptions)
  }, [])

  // rootProjectPlan が変わった時にフィルターオプションを再取得
  // eslint-disable-next-line react-hooks/exhaustive-deps -- rootProjectPlan の変更のみをトリガーとする。getOptions/getValuesForOptionFilter はレンダーごとに新規参照になるため deps に入れない
  useEffect(() => {
    if (getValuesForOptionFilter) {
      getOptions().then(setOptions)
    }
  }, [context.rootProjectPlan?.uuid])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.isAdded) return true
      if (!model) return true

      const selectValue = valueGetter
        ? valueGetter({ ...params, context })
        : api.getCellValue({ rowNode: params.node, colKey: colDef.field! })
      if (
        model.includeBlank &&
        (!selectValue ||
          (Array.isArray(selectValue) && selectValue.length === 0))
      ) {
        return true
      }
      if (
        Array.isArray(selectValue) &&
        selectValue.some(v => model.values.includes(getValue(v)))
      ) {
        return true
      } else if (model.values.includes(getValue(selectValue))) {
        return true
      }
      return false
    },

    afterGuiAttached() {
      getOptions().then(setOptions)
    },

    getModelAsString() {
      if (!model) return ''
      let labelList: string[] = []
      model.includeBlank && labelList.push('(Blank)')
      labelList = labelList.concat(
        options
          .filter(option => model.values.includes(getValue(option)))
          .map(option => getLabel(option))
      )
      return labelList.join(',')
    },
  })

  const getOptions = async () => {
    // getValuesForOptionFilter が定義されている場合、APIから有効なUUIDリストを取得
    if (getValuesForOptionFilter && optionKey && context[optionKey]) {
      const validUuids = await getValuesForOptionFilter(context)
      const contextOptions = context[optionKey] as any[]
      const filteredOptions = contextOptions.filter(opt =>
        validUuids.includes(getValue(opt))
      )
      if (sortValues) {
        return sortValues(
          colDef.cellRendererParams?.uiMeta,
          filteredOptions,
          context
        )
      }
      return filteredOptions
    }

    // 従来の動作: 行データからオプションを収集
    const initialOptions: any[] = []
    api.forEachNode(node => {
      initialOptions.push(
        valueGetter
          ? valueGetter({ node, context })
          : api.getCellValue({ rowNode: node, colKey: colDef.field! })
      )
    })
    const newOptionValues: string[] = []
    const newOptions: any[] = []
    initialOptions
      .filter(option => !!option)
      .forEach(option => {
        if (Array.isArray(option)) {
          option.forEach(o => {
            if (!newOptionValues.includes(getValue(o))) {
              newOptions.push(o)
              newOptionValues.push(getValue(o))
            }
          })
        } else if (!newOptionValues.includes(getValue(option))) {
          newOptions.push(option)
          newOptionValues.push(getValue(option))
        }
      })
    if (sortValues) {
      return sortValues(colDef.cellRendererParams?.uiMeta, newOptions, context)
    } else {
      return newOptions
    }
  }

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(
      buildSelectFilterModel(newValues, includeBlank, allOptionValues)
    )
  }

  const updateIncludeBlank = (newIncludeBlank: boolean) => {
    onModelChange(
      buildSelectFilterModel(values, newIncludeBlank, allOptionValues)
    )
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={includeBlank}
                  onChange={e => updateIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(
              option =>
                !text ||
                (valueFormatter
                  ? valueFormatter({
                      colDef,
                      value: getLabel(option),
                    }).includes(text)
                  : getLabel(option).includes(text))
            )
            .map(option => {
              const optionValue = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${optionValue}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {valueFormatter
                          ? valueFormatter({
                              colDef,
                              value: getLabel(option),
                            })
                          : getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={values.includes(optionValue)}
                      onChange={e => {
                        if (e.target.checked) {
                          updateValues([...values, optionValue])
                        } else {
                          updateValues(values.filter(v => v !== optionValue))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}

export const SelectFilterFormControl = styled(FormControlLabel)({
  margin: 0,
})

export const SelectFilterArea = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  margin: '0 3px',
})

export const CheckboxArea = styled(Box)({
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  marginLeft: '20px',
})

export const FilterLabel = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: '2px',
})
